import React, { Component } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  ButtonGroup,
  Input,
  FormGroup,
  Label,
  Spinner,
  Table,
} from "reactstrap";
import { withRouter, Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getRevenueReport, getRevenueTrends, exportRevenueAnalytics } from "../../Endpoint/Admin";
import { downloadBase64File } from "../../utils/exportToExcel";
import { toast } from "react-toastify";

class RevenueReports extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      groupBy: "day",
      startDate: this.getDefaultStartDate(),
      endDate: this.getDefaultEndDate(),
      revenueData: null,
      trendsData: null,
      breadcrumbItems: [
        { title: "Analytics", link: "/analytics" },
        { title: "Revenue Reports", link: "#" },
      ],
    };
  }

  getDefaultStartDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split("T")[0];
  };

  getDefaultEndDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  componentDidMount() {
    this.loadRevenueData();
  }

  loadRevenueData = async () => {
    this.setState({ loading: true });
    const { startDate, endDate, groupBy } = this.state;

    const [revenueResponse, trendsResponse] = await Promise.all([
      getRevenueReport({ startDate, endDate, groupBy }),
      getRevenueTrends("month"),
    ]);

    this.setState({
      revenueData: revenueResponse?.success ? revenueResponse.data : null,
      trendsData: trendsResponse?.success ? trendsResponse.data : null,
      loading: false,
    });
  };

  handleGroupByChange = (groupBy) => {
    this.setState({ groupBy }, this.loadRevenueData);
  };

  handleDateChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handleApplyFilters = () => {
    this.loadRevenueData();
  };

  handleExport = async () => {
    this.setState({ exporting: true });
    try {
      const { startDate, endDate } = this.state;
      const res = await exportRevenueAnalytics({ startDate, endDate });
      if (res?.success && res.data) {
        downloadBase64File(res.data.base64, res.data.fileName);
      } else {
        toast.error("Export failed");
      }
    } catch (err) {
      toast.error("Export failed");
    }
    this.setState({ exporting: false });
  };

  formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  renderGrowthIndicator = (growth) => {
    if (!growth && growth !== 0) return null;
    const isPositive = growth >= 0;
    return (
      <span className={`${isPositive ? "text-success" : "text-danger"}`}>
        <i className={`ri-arrow-${isPositive ? "up" : "down"}-line`}></i>
        {Math.abs(growth).toFixed(1)}%
      </span>
    );
  };

  render() {
    const { loading, groupBy, startDate, endDate, revenueData, trendsData } = this.state;

    const chartData = revenueData?.chartData || [];
    const totals = revenueData?.totals || {};
    const growth = trendsData?.growth || {};

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs title="Revenue Reports" breadcrumbItems={this.state.breadcrumbItems} />

            {/* Filters */}
            <Card>
              <CardBody>
                <Row className="align-items-end">
                  <Col md={2}>
                    <FormGroup className="mb-0">
                      <Label>Group By</Label>
                      <ButtonGroup className="d-block">
                        {["day", "week", "month"].map((g) => (
                          <Button
                            key={g}
                            size="sm"
                            color={groupBy === g ? "primary" : "light"}
                            onClick={() => this.handleGroupByChange(g)}
                          >
                            {g.charAt(0).toUpperCase() + g.slice(1)}
                          </Button>
                        ))}
                      </ButtonGroup>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        name="startDate"
                        value={startDate}
                        onChange={this.handleDateChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        name="endDate"
                        value={endDate}
                        onChange={this.handleDateChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <Button color="primary" onClick={this.handleApplyFilters}>
                      Apply
                    </Button>
                    <Button color="success" className="ml-2" disabled={this.state.exporting} onClick={this.handleExport}>
                      {this.state.exporting ? <Spinner size="sm" className="mr-1" /> : <i className="ri-file-excel-2-line mr-1"></i>} Export XLS
                    </Button>
                  </Col>
                  <Col md={2} className="text-right">
                    <Link to="/analytics" className="btn btn-outline-secondary">
                      Back to Dashboard
                    </Link>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2">Loading revenue data...</p>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <Row>
                  <Col md={3}>
                    <Card className="bg-primary text-white">
                      <CardBody>
                        <h6 className="text-white-50">Total Revenue</h6>
                        <h3>{this.formatCurrency(totals.total)}</h3>
                        {this.renderGrowthIndicator(growth.total)}
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="bg-success text-white">
                      <CardBody>
                        <h6 className="text-white-50">Food Revenue</h6>
                        <h3>{this.formatCurrency(totals.food)}</h3>
                        {this.renderGrowthIndicator(growth.food)}
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="bg-warning text-white">
                      <CardBody>
                        <h6 className="text-white-50">Room Revenue</h6>
                        <h3>{this.formatCurrency(totals.room)}</h3>
                        {this.renderGrowthIndicator(growth.room)}
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="bg-info text-white">
                      <CardBody>
                        <h6 className="text-white-50">Transport Revenue</h6>
                        <h3>{this.formatCurrency(totals.transport)}</h3>
                        {this.renderGrowthIndicator(growth.transport)}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Revenue Table */}
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Revenue by Period</h4>
                    {chartData.length === 0 ? (
                      <div className="text-center py-5">
                        <i className="ri-bar-chart-line" style={{ fontSize: "48px", color: "#ccc" }}></i>
                        <p className="text-muted mt-2">No revenue data for selected period</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table-hover mb-0">
                          <thead className="thead-light">
                            <tr>
                              <th>Period</th>
                              <th className="text-right">Food</th>
                              <th className="text-right">Room</th>
                              <th className="text-right">Transport</th>
                              <th className="text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {chartData.map((item, index) => (
                              <tr key={index}>
                                <td><strong>{item.period}</strong></td>
                                <td className="text-right text-success">
                                  {this.formatCurrency(item.food)}
                                </td>
                                <td className="text-right text-warning">
                                  {this.formatCurrency(item.room)}
                                </td>
                                <td className="text-right text-info">
                                  {this.formatCurrency(item.transport)}
                                </td>
                                <td className="text-right">
                                  <strong>{this.formatCurrency(item.total)}</strong>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-light">
                            <tr>
                              <th>TOTAL</th>
                              <th className="text-right text-success">
                                {this.formatCurrency(totals.food)}
                              </th>
                              <th className="text-right text-warning">
                                {this.formatCurrency(totals.room)}
                              </th>
                              <th className="text-right text-info">
                                {this.formatCurrency(totals.transport)}
                              </th>
                              <th className="text-right">
                                <strong>{this.formatCurrency(totals.total)}</strong>
                              </th>
                            </tr>
                          </tfoot>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </>
            )}
          </Container>
        </div>
      </React.Fragment>
    );
  }
}

export default withRouter(withTranslation()(RevenueReports));
